/**
 * Lab View
 * AI pick testing analytics dashboard
 *
 * Shows comprehensive analytics on AI-generated picks performance:
 * - Overall stats
 * - Performance by config, tier, prop type, direction
 * - AI edge, confidence, trend analysis
 * - Player performance (top/worst)
 * - Optimal strategy combinations
 * - B2B and blowout risk analysis
 * - Generator runner for testing configs
 *
 * Supports two analytics modes:
 * - Picks: Leg-by-leg analysis (individual pick predictions)
 * - Parlays: Parlay-level analysis (whole parlay win/loss)
 */
import { type ReactElement, useState } from 'react';
import { ComboPerformanceTable } from './components/ComboPerformanceTable';
import { DailyTimeline } from './components/DailyTimeline';
import { FactorPerformanceTable } from './components/FactorPerformanceTable';
import { GeneratorRunner } from './components/GeneratorRunner';
import { LabFilterBar } from './components/LabFilterBar';
import { MarketInsightsPanel } from './components/MarketInsightsPanel';
import { ParlayInsightsPanel } from './components/ParlayInsightsPanel';
import { ParlayQuickStatsGrid } from './components/ParlayQuickStatsGrid';
import { ParlaysAnalyticsGrid } from './components/ParlaysAnalyticsGrid';
import { PicksAnalyticsGrid } from './components/PicksAnalyticsGrid';
import { PlayerPerformanceTable } from './components/PlayerPerformanceTable';
import { QuickStatsGrid } from './components/QuickStatsGrid';
import { SettlementResultsModal } from './components/SettlementResultsModal';
import { TwoTierInsightsPanel } from './components/TwoTierInsightsPanel';
import {
  useDailyPerformance,
  useFactorPerformance,
  useLabDashboard,
  useLabSettlement,
  useLegOriginPerformance,
  useMarketInsights,
  useParlayDashboard,
  useParlayInsights,
  useParlayPerformance,
  useTwoTierAnalytics,
  useUsersWithTrackedPicks,
} from './hooks/useLabDashboard';

// =============================================================================
// Types
// =============================================================================

type LabTab = 'analytics' | 'insights' | 'generator';
type AnalyticsMode = 'picks' | 'parlays';

// =============================================================================
// Component
// =============================================================================

export function LabView(): ReactElement {
  const [activeTab, setActiveTab] = useState<LabTab>('analytics');
  const [analyticsMode, setAnalyticsMode] = useState<AnalyticsMode>('picks');
  const { data, loading, error, filters, setFilters, refresh } = useLabDashboard();
  const twoTier = useTwoTierAnalytics(filters);
  const { users: availableUsers } = useUsersWithTrackedPicks();
  const dailyPerformance = useDailyPerformance(filters);
  const parlayPerformance = useParlayPerformance(filters);
  const parlayDashboard = useParlayDashboard(filters);
  const parlayInsights = useParlayInsights(filters);
  const legOriginPerformance = useLegOriginPerformance(filters);
  const factorPerformance = useFactorPerformance(filters);
  const settlement = useLabSettlement();
  const marketInsights = useMarketInsights(14, filters);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-primary mx-auto mb-4" />
          <p className="text-theme-text-secondary">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <p className="text-theme-danger mb-4">{error}</p>
          <button
            type="button"
            onClick={refresh}
            className="px-4 py-2 bg-theme-primary-fill text-theme-on-primary-fill rounded hover:bg-theme-primary-fill-hover transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-theme-text-secondary">No data available</p>
      </div>
    );
  }

  return (
    <div data-testid="lab-view" className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-theme-text">Lab</h1>
          <p className="text-theme-text-secondary text-sm mt-1">AI pick testing & analytics</p>
        </div>
        {(activeTab === 'analytics' || activeTab === 'insights') && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={async () => {
                const result = await settlement.runSettlement();
                if (result) {
                  refresh();
                  marketInsights.refresh();
                }
              }}
              disabled={settlement.isSettling}
              className="px-3 py-1.5 bg-theme-success text-white rounded text-sm hover:bg-theme-success/80 transition-colors"
            >
              {settlement.isSettling ? 'Settling...' : 'Settle Now'}
            </button>
            <button
              type="button"
              onClick={() => {
                refresh();
                if (activeTab === 'insights') marketInsights.refresh();
              }}
              disabled={loading}
              className="px-3 py-1.5 bg-theme-bg text-theme-text-secondary rounded text-sm hover:bg-theme-border transition-colors"
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-theme-border pb-2">
        <button
          type="button"
          data-testid="lab-tab-analytics"
          data-active={activeTab === 'analytics'}
          onClick={() => setActiveTab('analytics')}
          className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${
            activeTab === 'analytics'
              ? 'bg-theme-surface text-theme-text'
              : 'text-theme-text-secondary hover:text-theme-text'
          }`}
        >
          Analytics
        </button>
        <button
          type="button"
          data-testid="lab-tab-insights"
          data-active={activeTab === 'insights'}
          onClick={() => setActiveTab('insights')}
          className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${
            activeTab === 'insights'
              ? 'bg-theme-surface text-theme-text'
              : 'text-theme-text-secondary hover:text-theme-text'
          }`}
        >
          Market Insights
        </button>
        <button
          type="button"
          data-testid="lab-tab-generator"
          data-active={activeTab === 'generator'}
          onClick={() => setActiveTab('generator')}
          className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${
            activeTab === 'generator'
              ? 'bg-theme-surface text-theme-text'
              : 'text-theme-text-secondary hover:text-theme-text'
          }`}
        >
          Generator
        </button>
      </div>

      {/* Generator Tab - always mounted but hidden when not active to preserve state */}
      <div className={activeTab === 'generator' ? '' : 'hidden'}>
        <GeneratorRunner />
      </div>

      {/* Market Insights Tab */}
      {activeTab === 'insights' && (
        <MarketInsightsPanel
          data={marketInsights.data}
          loading={marketInsights.loading}
          error={marketInsights.error}
          generating={marketInsights.generating}
          onGenerateInsights={marketInsights.generateInsights}
          onRefresh={marketInsights.refresh}
          scope={marketInsights.scope}
          onScopeChange={marketInsights.setScope}
        />
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <>
          {/* Analytics Mode Toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 bg-theme-surface rounded-lg p-1">
              <button
                type="button"
                onClick={() => setAnalyticsMode('picks')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  analyticsMode === 'picks'
                    ? 'bg-theme-primary-fill text-theme-on-primary-fill'
                    : 'text-theme-text-secondary hover:text-theme-text'
                }`}
              >
                Picks
              </button>
              <button
                type="button"
                onClick={() => {
                  setAnalyticsMode('parlays');
                  // Clear pick-specific filters when switching to parlays mode
                  setFilters({
                    config: '',
                    tier: '',
                    propType: '',
                    direction: '',
                    minSampleSize: 5,
                  });
                }}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  analyticsMode === 'parlays'
                    ? 'bg-theme-primary-fill text-theme-on-primary-fill'
                    : 'text-theme-text-secondary hover:text-theme-text'
                }`}
              >
                Parlays
              </button>
            </div>
            <p className="text-xs text-theme-text-tertiary">
              {analyticsMode === 'picks' ? 'Leg-by-leg analysis' : 'Parlay-level analysis'}
            </p>
          </div>

          {/* Filters - show pick-level filters (config/tier/propType/direction) only for picks mode */}
          <LabFilterBar
            filters={filters}
            setFilters={setFilters}
            availableConfigs={analyticsMode === 'picks' ? data.filters.availableConfigs : []}
            availablePropTypes={analyticsMode === 'picks' ? data.filters.availablePropTypes : []}
            availableUsers={availableUsers}
            showPickFilters={analyticsMode === 'picks'}
          />

          {/* Quick Stats - different component for each mode */}
          {analyticsMode === 'picks' ? (
            <QuickStatsGrid summary={data.summary} />
          ) : (
            parlayDashboard.data && <ParlayQuickStatsGrid summary={parlayDashboard.data.summary} />
          )}

          {/* Dynamic Insights (Two-Tier Analytics) - only for picks mode */}
          {analyticsMode === 'picks' && twoTier.data && (
            <TwoTierInsightsPanel
              data={twoTier.data}
              loading={twoTier.loading}
              recentDays={twoTier.recentDays}
              setRecentDays={twoTier.setRecentDays}
            />
          )}

          {/* Parlay Insights - only for parlays mode */}
          {analyticsMode === 'parlays' && parlayInsights.data && (
            <ParlayInsightsPanel
              data={parlayInsights.data}
              loading={parlayInsights.loading}
              recentDays={parlayInsights.recentDays}
              setRecentDays={parlayInsights.setRecentDays}
            />
          )}

          {/* Main Analytics Grid */}
          <div className="grid @5xl:grid-cols-2 gap-6">
            {analyticsMode === 'picks' && (
              <PicksAnalyticsGrid data={data} legOriginPerformance={legOriginPerformance} />
            )}
            {analyticsMode === 'parlays' && parlayDashboard.data && (
              <ParlaysAnalyticsGrid data={parlayDashboard.data} />
            )}
          </div>

          {/* Factor Performance (ON vs OFF) - only for picks mode */}
          {analyticsMode === 'picks' && (
            <div className="grid @5xl:grid-cols-1 gap-6">
              <FactorPerformanceTable
                data={factorPerformance.data}
                loading={factorPerformance.loading}
              />
            </div>
          )}

          {/* Optimal Strategies - only for picks mode */}
          {analyticsMode === 'picks' && data.comboPerformance.length > 0 && (
            <ComboPerformanceTable
              title="Optimal Strategies (Tier + Prop + Direction)"
              data={data.comboPerformance}
            />
          )}

          {/* Player Performance - only for picks mode */}
          {analyticsMode === 'picks' && (
            <div className="grid @5xl:grid-cols-2 gap-6">
              {data.topPlayers.length > 0 && (
                <PlayerPerformanceTable
                  title="Top Players"
                  data={data.topPlayers}
                  variant="success"
                />
              )}
              {data.worstPlayers.length > 0 && (
                <PlayerPerformanceTable
                  title="Worst Players"
                  data={data.worstPlayers}
                  variant="danger"
                />
              )}
            </div>
          )}

          {/* Daily Performance */}
          <DailyTimeline
            mode={analyticsMode}
            pickData={dailyPerformance.data}
            parlayData={parlayPerformance.data}
            pickLoading={dailyPerformance.loading}
            parlayLoading={parlayPerformance.loading}
            pickError={dailyPerformance.error}
            parlayError={parlayPerformance.error}
          />

          {/* Footer */}
          <div className="text-center text-xs text-theme-text-tertiary">
            Last updated: {new Date(data.generatedAt).toLocaleString()}
          </div>
        </>
      )}

      {/* Settlement Results Modal - shows after settlement completes */}
      {(settlement.lastResult || settlement.error) && (
        <SettlementResultsModal
          result={
            settlement.lastResult || {
              success: false,
              legsSettled: 0,
              parlaysSettled: 0,
              legsProcessed: 0,
              parlaysUpdated: 0,
            }
          }
          error={settlement.error}
          onClose={settlement.clearLastResult}
        />
      )}
    </div>
  );
}
